import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useAdminStore } from '@/stores/adminStore';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Spinner } from '@/components/common/Spinner';
import * as adminApi from '@/api/admin';
import type { ReleasePolicyResponse } from '@/types/admin';
import type { ReleaseEntry } from '@/api/releases';
import { classifyPin } from '@/utils/versionDistance';

export default function AdminReleasesPage() {
  const { admin, clearAdmin } = useAdminStore();

  const [policy, setPolicy] = useState<ReleasePolicyResponse | null>(null);
  const [releases, setReleases] = useState<ReleaseEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [p, r] = await Promise.all([
        adminApi.getReleasePolicy(),
        adminApi.listAdminReleases(),
      ]);
      setPolicy(p);
      setReleases(r.releases);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load release data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } finally {
      clearAdmin();
    }
  };

  const isPinned = Boolean(policy?.pinVersion);

  return (
    <div className="min-h-screen bg-md-cream py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-md-graphite">
              Desktop Release Policy
            </h1>
            <p className="text-sm text-md-slate mt-1">
              Signed in as {admin?.email} ({admin?.role})
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-sm font-medium text-md-sky-strong hover:text-md-graphite transition-colors"
            >
              ← Admin
            </Link>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-md-error-bg border border-md-error text-md-error p-4 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-md-success-bg border border-md-success text-md-success p-4 rounded mb-6">
            {success}
          </div>
        )}

        <Card className="mb-6">
          <h2 className="text-xl font-bold text-md-graphite mb-4">
            Current State
          </h2>
          {isLoading ? (
            <div className="flex items-center gap-3 text-sm text-md-slate">
              <Spinner size="sm" />
              <span>Loading release policy…</span>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-md-slate">latest.json points to: </span>
                <span className="font-mono text-md-graphite">
                  {policy?.latestVersion ?? '—'}
                </span>
              </div>
              <div>
                <span className="text-md-slate">Pin status: </span>
                {isPinned ? (
                  <span className="text-md-warning font-medium">
                    Pinned to {policy?.pinVersion}
                  </span>
                ) : (
                  <span className="text-md-success font-medium">
                    Not pinned (normal latest.json flow)
                  </span>
                )}
              </div>
              {isPinned && policy?.reason && (
                <div>
                  <span className="text-md-slate">Reason: </span>
                  <span className="text-md-graphite">{policy.reason}</span>
                </div>
              )}
              {isPinned && policy?.pinnedBy && (
                <div>
                  <span className="text-md-slate">Pinned by: </span>
                  <span className="text-md-graphite">{policy.pinnedBy}</span>
                  {policy.pinnedAt && (
                    <span className="text-md-slate">
                      {' '}
                      on {new Date(policy.pinnedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        {!isLoading &&
          (isPinned ? (
            <ClearPinCard
              currentPin={policy?.pinVersion ?? ''}
              onSuccess={(msg) => {
                setSuccess(msg);
                setError(null);
                load();
              }}
              onError={(msg) => {
                setError(msg);
                setSuccess(null);
              }}
            />
          ) : (
            <SetPinCard
              releases={releases}
              latestVersion={policy?.latestVersion ?? null}
              onSuccess={(msg) => {
                setSuccess(msg);
                setError(null);
                load();
              }}
              onError={(msg) => {
                setError(msg);
                setSuccess(null);
              }}
            />
          ))}
      </div>
    </div>
  );
}

interface SetPinCardProps {
  releases: ReleaseEntry[];
  latestVersion: string | null;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function SetPinCard({
  releases,
  latestVersion,
  onSuccess,
  onError,
}: SetPinCardProps) {
  // Exclude the current latest from the pin dropdown — pinning to what
  // latest.json already points to has no effect.
  const pinnable = useMemo(
    () => releases.filter((r) => r.version !== latestVersion),
    [releases, latestVersion]
  );

  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Card>
      <h2 className="text-xl font-bold text-md-graphite mb-2">Pin a Version</h2>
      <p className="text-sm text-md-slate mb-4">
        Clients checking for updates will be told to install the pinned
        version instead of whatever <code>latest.json</code> points to.
        Propagation: ~1 min policy cache + ~5 min CDN cache + ~5 min client
        poll.
      </p>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-md-ink mb-2">
            Version
          </label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full px-4 py-2 border-2 border-md-graphite rounded bg-md-fog text-md-ink focus:outline-none focus:border-md-sky-strong"
          >
            <option value="">Select a version…</option>
            {pinnable.map((r) => (
              <option key={r.version} value={r.version}>
                {r.version}
                {r.date ? ` — ${new Date(r.date).toLocaleDateString()}` : ''}
              </option>
            ))}
          </select>
          {pinnable.length === 0 && (
            <p className="text-xs text-md-slate mt-1">
              No other releases available to pin.
            </p>
          )}
        </div>

        <Input
          label="Reason (optional, recorded for audit)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Rollback: crash in 0.13.3-daily.20260421.55"
        />

        <div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={!selected}
          >
            Pin Version
          </Button>
        </div>
      </div>

      <PinConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        targetVersion={selected}
        latestVersion={latestVersion}
        reason={reason}
        onSuccess={(msg) => {
          setSelected('');
          setReason('');
          onSuccess(msg);
        }}
        onError={onError}
      />
    </Card>
  );
}

interface PinConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetVersion: string;
  latestVersion: string | null;
  reason: string;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function PinConfirmDialog({
  open,
  onOpenChange,
  targetVersion,
  latestVersion,
  reason,
  onSuccess,
  onError,
}: PinConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset the typed field whenever the dialog opens for a (potentially new)
  // target so a stale match can't sneak through.
  useEffect(() => {
    if (open) setTyped('');
  }, [open, targetVersion]);

  const distance = useMemo(
    () => classifyPin(targetVersion, latestVersion),
    [targetVersion, latestVersion]
  );

  const matches = typed === targetVersion;

  const handleConfirm = async () => {
    if (!matches) return;
    setIsSaving(true);
    try {
      const response = await adminApi.setReleasePolicy(
        targetVersion,
        reason.trim() || undefined
      );
      onSuccess(response.message);
      onOpenChange(false);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to set pin');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-md-cream border-2 border-md-graphite rounded-lg shadow-xl p-6 z-50">
          <Dialog.Title className="text-xl font-bold text-md-graphite mb-2">
            Confirm pin to {targetVersion}
          </Dialog.Title>
          <Dialog.Description className="text-sm text-md-slate mb-4">
            All desktop clients on other versions will be switched to{' '}
            <span className="font-mono">{targetVersion}</span> on their next
            update check (~10 min end-to-end).
          </Dialog.Description>

          {distance.kind === 'far' && (
            <div className="bg-md-warning-bg border border-md-warning text-md-ink p-3 rounded mb-4 text-sm">
              <strong className="block mb-1 text-md-warning">
                Large rollback distance
              </strong>
              {distance.reason}{' '}
              Make sure this is what you intend — migrations, feature flags,
              or IPC shapes may have changed.
            </div>
          )}

          {distance.kind === 'unknown' && (
            <div className="bg-md-warning-bg border border-md-warning text-md-ink p-3 rounded mb-4 text-sm">
              <strong className="block mb-1 text-md-warning">
                Couldn't assess distance
              </strong>
              {distance.reason}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-md-ink mb-2">
              Type the target version to confirm:
            </label>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={targetVersion}
              autoFocus
            />
            {typed && !matches && (
              <p className="text-xs text-md-error mt-1">
                Doesn't match — expected{' '}
                <span className="font-mono">{targetVersion}</span>.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Dialog.Close asChild>
              <Button variant="secondary" size="sm" disabled={isSaving}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={!matches}
              loading={isSaving}
            >
              Pin {targetVersion}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ClearPinCardProps {
  currentPin: string;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function ClearPinCard({ currentPin, onSuccess, onError }: ClearPinCardProps) {
  const [isClearing, setIsClearing] = useState(false);

  const handleClear = async () => {
    if (
      !confirm(
        `Clear pin for ${currentPin}? Clients will resume following latest.json on the next update check.`
      )
    ) {
      return;
    }
    setIsClearing(true);
    try {
      const response = await adminApi.clearReleasePolicy();
      onSuccess(response.message);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to clear pin');
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Card>
      <h2 className="text-xl font-bold text-md-graphite mb-2">Clear Pin</h2>
      <p className="text-sm text-md-slate mb-4">
        Remove the pin and resume the normal <code>latest.json</code> flow.
        Clients that were downgraded will upgrade back to latest on the next
        poll.
      </p>
      <Button
        variant="danger"
        size="sm"
        onClick={handleClear}
        loading={isClearing}
      >
        Clear Pin
      </Button>
    </Card>
  );
}
